import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import Vue from 'vue'

@Module({
  namespaced: true
})
export default class OpportunitiesStore extends VuexModule {
  funnelsLoaded = false
  funnels: KwixeoOpportunities.Funnels = []
  lossReasonsLoaded = false
  lossReasons: KwixeoOpportunities.LossReasons = []

  // CLEAR STORE STATE
  @Action
  clearStore (): void {
    const elements = ['Funnels', 'LossReasons']
    elements.forEach(key => {
      this.context.commit('set' + key, [])
      this.context.commit('set' + key + 'Loaded', false)
    })
  }

  // Sort a list alphabetically
  @Mutation
  orderList (param: { list: KwixeoInterface.GenericObject[]; key: string }): void {
    const key = param.key
    param.list.sort((a, b) => {
      const aKey = a[key].toLowerCase()
      const bKey = b[key].toLowerCase()
      if (aKey < bKey) return -1
      else if (aKey > bKey) return 1
      else return 0
    })
  }

  get funnelStep (): (id: number) => KwixeoForm.SelectOptions {
    return id => this.funnels.find(funnel => funnel.id === id)?.steps ?? []
  }

  get steps (): KwixeoForm.SelectOptions {
    const options: KwixeoForm.SelectOptions = []
    this.funnels.forEach(funnel => {
      options.push({
        id: 'funnel-' + funnel.id,
        name: funnel.name,
        disable: true
      })

      funnel.steps?.forEach(step => {
        options.push(step)
      })
    })
    return options
  }

  get categories (): (funnelId: number) => KwixeoOpportunities.FunnelCategories {
    return (funnelId: number) => {
      return this.funnels.find(funnel => funnel.id === funnelId)?.categories ?? []
    }
  }

  get allCategories (): KwixeoForm.SelectOptions {
    const options: KwixeoForm.SelectOptions = []
    this.funnels.forEach(funnel => {
      options.push({
        id: 'funnel-' + funnel.id,
        label: funnel.name,
        disable: true
      })

      funnel.categories?.forEach(category => {
        options.push(category)
      })
    })
    return options
  }

  get availableFunnels (): KwixeoOpportunities.Funnels {
    return this.funnels.filter(funnel => {
      return !funnel.restricted || (funnel.team_users?.findIndex(user => user.id === this.context.rootGetters['user/teamUser'].id) ?? -1) >= 0
    })
  }

  // Change the loaded state
  @Mutation
  setFunnelsLoaded (state = true): void {
    this.funnelsLoaded = state
  }

  // Set the funnels
  @Mutation
  setFunnels (funnels: KwixeoOpportunities.Funnels): void {
    this.funnels = funnels
  }

  // Add or update a funnel
  @Mutation
  updateFunnel (funnel: KwixeoOpportunities.Funnel): void {
    if (!this.funnelsLoaded) {
      return
    }
    const index = this.funnels.findIndex(type => type.id === funnel.id)
    if (index >= 0) {
      Vue.set(this.funnels, index, funnel)
    } else {
      this.funnels.push(funnel)
    }
  }

  // Remove a funnel
  @Mutation
  removeFunnel (funnel: KwixeoOpportunities.Funnel): void {
    if (!this.funnelsLoaded) {
      return
    }
    const index = this.funnels.findIndex(type => type.id === funnel.id)
    if (index >= 0) {
      Vue.delete(this.funnels, index)
    }
  }

  // Load the funnels from server
  @Action
  async loadFunnels (force = false): Promise<void> {
    if (!this.funnelsLoaded || force) {
      const response = await Vue.prototype.$kwOpportunities.axios.get('/opportunity/funnel?include=steps,categories,team_users&per_page=500&sort=name')
      await this.context.commit('setFunnels', response.data.data)
      this.context.commit('setFunnelsLoaded')
    }
  }

  // Add or update a funnel
  @Action
  setFunnel (funnel: KwixeoOpportunities.Funnel): void {
    this.context.commit('updateFunnel', funnel)
    if (this.funnelsLoaded) {
      this.context.commit('orderList', { list: this.funnels, key: 'name' })
    }
  }

  // Delete a funnel
  @Action
  deleteFunnel (funnel: KwixeoOpportunities.Funnel): void {
    this.context.commit('removeFunnel', funnel)
  }


  // Change the loaded state
  @Mutation
  setLossReasonsLoaded (state = true): void {
    this.lossReasonsLoaded = state
  }

  // Set the loss reasons
  @Mutation
  setLossReasons (lossReasons: KwixeoOpportunities.LossReasons): void {
    this.lossReasons = lossReasons
  }

  // Add or update a loss reason
  @Mutation
  updateLossReason (lossReason: KwixeoOpportunities.LossReason): void {
    if (!this.lossReasonsLoaded) {
      return
    }
    const index = this.lossReasons.findIndex(type => type.id === lossReason.id)
    if (index >= 0) {
      Vue.set(this.lossReasons, index, lossReason)
    } else {
      this.lossReasons.push(lossReason)
    }
  }

  // Remove a loss reason
  @Mutation
  removeLossReason (lossReason: KwixeoOpportunities.LossReason): void {
    if (!this.lossReasonsLoaded) {
      return
    }
    const index = this.lossReasons.findIndex(type => type.id === lossReason.id)
    if (index >= 0) {
      Vue.delete(this.lossReasons, index)
    }
  }

  // Load the loss reasons from server
  @Action
  async loadLossReasons (force = false): Promise<void> {
    if (!this.lossReasonsLoaded || force) {
      const response = await Vue.prototype.$kwOpportunities.axios.get('/opportunity/loss-reason?per_page=500&sort=label')
      await this.context.commit('setLossReasons', response.data.data)
      this.context.commit('setLossReasonsLoaded')
    }
  }

  // Add or update a loss reason
  @Action
  setLossReason (lossReason: KwixeoOpportunities.LossReason): void {
    this.context.commit('updateLossReason', lossReason)
    if (this.lossReasonsLoaded) {
      this.context.commit('orderList', { list: this.lossReasons, key: 'label' })
    }
  }

  // Delete a loss reason
  @Action
  deleteLossReason (lossReason: KwixeoOpportunities.LossReason): void {
    this.context.commit('removeLossReason', lossReason)
  }
}
